import React from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import '../styles/AgentDashboard.css';
import PaymentForm from './PaymentForm';
import { API_BASE_URL, STRIPE_PUBLISHABLE_KEY } from '../config';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../utils/api';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function AgentDashboard() {
  const { user } = useAuth();
  const [prd, setPrd] = React.useState('');
  const [githubLink, setGithubLink] = React.useState('');
  const [bountyPool, setBountyPool] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState('general');
  const [clientSecret, setClientSecret] = React.useState('');
  const [showPaymentForm, setShowPaymentForm] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleRunAgent = async () => {
    if (!user) {
      setError('You must be logged in to create a project');
      return;
    }

    if (!title.trim() || !description.trim() || !bountyPool.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (isNaN(bountyPool) || parseFloat(bountyPool) <= 0) {
      setError('Please enter a valid bounty amount');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create the problem and get payment intent
      const response = await fetchWithAuth(`${API_BASE_URL}/problems`, {
        method: 'POST',
        body: JSON.stringify({
          agent_id: user.id,
          title,
          description,
          category,
          bounty_amount: parseFloat(bountyPool),
          github_repo_url: githubLink,
          prd
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create project');
      }

      setClientSecret(data.clientSecret);
      setShowPaymentForm(true);
    } catch (err) {
      console.error('Error creating problem:', err);
      setError(err.message || 'Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    console.log('Payment successful!', paymentIntent);
    // Reset form
    setTitle('');
    setDescription('');
    setPrd('');
    setGithubLink('');
    setBountyPool('');
    setCategory('general');
    setShowPaymentForm(false);
    setClientSecret('');

    // Show success message
    alert('Project created and payment successful! Your project is now live.');
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setError('Payment failed. Please try again.');
    setShowPaymentForm(false);
  };

  const handleCancelPayment = () => {
    setShowPaymentForm(false);
    setClientSecret('');
  };

  if (showPaymentForm && clientSecret) {
    return (
      <div className="agent-dashboard">
        <h1>Complete Payment</h1>
        <div className="payment-container">
          <button
            className="back-button"
            onClick={handleCancelPayment}
          >
            &larr; Back to Edit
          </button>

          <Elements stripe={stripePromise}>
            <PaymentForm
              clientSecret={clientSecret}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              amount={bountyPool}
            />
          </Elements>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard">
      <h1>Agent Dashboard</h1>
      <p className="dashboard-subtitle">Create a new project with a Product Requirements Document</p>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="agent-controls">
        <div className="form-group">
          <label htmlFor="title">Project Title *</label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a descriptive title for your project"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Project Description *</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what you want to build..."
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="general">General</option>
            <option value="web">Web Development</option>
            <option value="mobile">Mobile Development</option>
            <option value="backend">Backend Development</option>
            <option value="ai-ml">AI/ML</option>
            <option value="data">Data Science</option>
            <option value="devops">DevOps</option>
          </select>
        </div>

        <div className="prd-input form-group">
          <label htmlFor="prd">Product Requirements Document (PRD)</label>
          <textarea
            id="prd"
            value={prd}
            onChange={(e) => setPrd(e.target.value)}
            placeholder="Enter detailed requirements, user stories, acceptance criteria..."
            rows={10}
          />
        </div>

        <div className="github-input form-group">
          <label htmlFor="github">GitHub Repository Link</label>
          <input
            id="github"
            type="url"
            value={githubLink}
            onChange={(e) => setGithubLink(e.target.value)}
            placeholder="https://github.com/username/repository (optional)"
          />
        </div>

        <div className="bounty-input form-group">
          <label htmlFor="bounty">Bounty Pool Amount ($) *</label>
          <input
            id="bounty"
            type="number"
            value={bountyPool}
            onChange={(e) => setBountyPool(e.target.value)}
            placeholder="Enter the total bounty pool amount"
            min="1"
            step="0.01"
          />
          <small className="input-hint">This amount will be charged to your card</small>
        </div>

        <button
          className="run-agent-button"
          onClick={handleRunAgent}
          disabled={loading || !title.trim() || !description.trim() || !bountyPool.trim()}
        >
          {loading ? 'Creating Project...' : 'Proceed to Payment'}
        </button>
      </div>
    </div>
  );
}

export default AgentDashboard; 